/**
 * useMarketplaceAccess.ts
 *
 * WHY THIS HOOK EXISTS
 * --------------------
 * Access to the Agentic Marketplace is gated by an RBAC read permission that
 * resolves asynchronously from the identity token. The page needs to
 * distinguish "still checking" from "resolved", and within "resolved" whether
 * the user is allowed. Keeping the async resolution, the mounted guard, and the
 * fail-closed policy inside the page mixes access concerns with rendering.
 *
 * This hook owns read-access resolution and exposes two derived values:
 *
 *   isLoading     — true until the permission check resolves. Drives the
 *                   loading spinner. Internally this is the "not yet resolved"
 *                   (undefined) condition made explicit.
 *   hasReadAccess — once resolved, whether the user has read access. Defaults
 *                   to false while loading and on any error (fail closed).
 */

import { useApi } from '@backstage/core-plugin-api';
import { useEffect, useState } from 'react';

import { agenticMarketplaceApiRef } from '../api/AgenticMarketplaceApi';

export interface UseMarketplaceAccessResult {
  /** True until the RBAC read-permission check resolves. */
  isLoading: boolean;
  /**
   * Whether the current user has read access. False while loading and on any
   * error (fail closed).
   */
  hasReadAccess: boolean;
}

export const useMarketplaceAccess = (): UseMarketplaceAccessResult => {
  const marketplaceApi = useApi(agenticMarketplaceApiRef);
  // undefined = unresolved; the public `isLoading` flag is derived from this.
  const [readAccess, setReadAccess] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    let mounted = true;

    marketplaceApi
      .checkPermissions()
      .then(perms => {
        if (mounted) {
          setReadAccess(perms.read);
        }
      })
      .catch(() => {
        // Fail closed: any error resolving the permission denies access.
        if (mounted) {
          setReadAccess(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [marketplaceApi]);

  return {
    isLoading: readAccess === undefined,
    hasReadAccess: readAccess ?? false,
  };
};

export default useMarketplaceAccess;
